import { LoadFailed, QueryLoading } from '../components/QueryState';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Button, Panel, PanelHeader } from '@promanage/ui';
import { nzDate } from '@promanage/core';
import { ReportWorkspace } from '../features/reports/ReportWorkspace';
import { LogFilters } from '../features/reports/LogFilters';
import { EventReport } from '../features/reports/EventReport';
import { ExportPdfButton } from '../features/reports/ExportPdfButton';
import { describeEventFilter, toEventQuery } from '../features/reports/eventFilter';
// The route's own search parsing is its own module, and stays free of the event vocabulary —
// `validateSearch` runs during routing, so anything it imports is in the eager bundle.
import { parseEventSearch, type EventFilter } from '../features/reports/eventSearch';
import { letterheadLogo } from '../features/branding/letterhead';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/reports/events')({
  // Shape-checked only — see `parseEventSearch`. A malformed range is refused by the contract,
  // which is the boundary a pasted URL crosses anyway, and the server's sentence beside the
  // filter beats a router error page.
  validateSearch: parseEventSearch,
  component: EventReportPage,
});

/**
 * The domain event log.
 *
 * WHAT IT IS FOR. Every state change in this product emits an event into an append-only outbox, in
 * the same transaction as the change itself (docs/AUTOMATION.md §3.2), so an event exists if and
 * only if the change committed. Nothing consumes them until M5 — that was always the plan, because
 * emitting is cheap now and a silent retrofit later — but the consequence was a table that has
 * been filling since M2 with no way to read it back. Two people need it before the first rule
 * exists: whoever writes that rule, who has to see the stream they are subscribing to, and whoever
 * is on call, who needs to know whether the drain is keeping up.
 *
 * IT IS THE AUDIT REPORT'S TWIN, ON PURPOSE. Same filter, same masthead, same paging, same two
 * ways out. The two tables are emphatically not the same thing — `audit_log` is hash-chained
 * evidence kept seven years, this is operational data kept two — but a manager should not have to
 * learn two documents to read two logs. Where they differ, they differ in what is on the row: no
 * ip, no user agent, no chain hash here, because an event carries none of them, and a delivery
 * stamp instead, because an event nobody has fanned out is one nothing has acted on.
 *
 * THE FILTER LIVES IN THE URL, like the audit report's. A report is something somebody reloads,
 * bookmarks, and sends to a colleague with "this is the bit I mean"; component state survives none
 * of the three. It also gives paging its reset for free — the filter is part of the query key, so
 * moving a date starts a fresh report rather than appending August to July.
 */
function EventReportPage() {
  const filter = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const today = nzDate(new Date());

  const actors = useQuery(
    // Its own read: the people who appear in the log do not change as the range does, and folding
    // this into the report would empty the options as the filter is used.
    trpc.reports.eventActors.queryOptions(undefined, {
      meta: { errorTitle: 'Could not load people for this filter' },
      staleTime: 5 * 60_000,
    }),
  );

  /*
   * Whose agency this is, for the printed masthead. The same query the account menu runs, so on
   * any session that has opened the menu this is a cache hit.
   */
  const orgs = useQuery(trpc.auth.organisations.queryOptions(undefined, { staleTime: 60_000 }));
  const orgName = orgs.data?.find((org) => org.isCurrent)?.name ?? null;

  /*
   * Keyset paging, by the outbox's own sequence.
   *
   * `getNextPageParam` reads the server's answer rather than counting rows: the read fetches one
   * more than the limit and reports `nextCursor` from it, so "is there another page" is settled by
   * the same query that returned this one. Two queries could disagree while somebody is working,
   * and on a log that fills itself as work is done that disagreement is not hypothetical.
   */
  const report = useInfiniteQuery(
    trpc.reports.events.infiniteQueryOptions(toEventQuery(filter), {
      meta: { errorTitle: 'We could not produce that report' },
      getNextPageParam: (page) => page.nextCursor,
      staleTime: 30_000,
    }),
  );

  const pages = report.data?.pages ?? [];
  const rows = pages.flatMap((page) => page.rows);
  // Counted once, on the first page — see `EventReport.total`. Later pages carry null.
  const total = pages[0]?.total ?? null;

  const actor = actors.data?.find((entry) => entry.userId === filter.actor);
  const actorName = actor?.name ?? actor?.email ?? null;

  return (
    <ReportWorkspace
      title="Event log"
      subtitle={describeEventFilter(filter, actorName)}
      actions={
        <>
          {/*
           * TWO WAYS OUT, and they are not redundant — the reasoning is written out in full on
           * the audit report, which this deliberately mirrors. Print costs nothing to offer and
           * cannot number its own pages; the PDF renderer knows how many it made, and is worth
           * ~130 KB to the person filing one and nothing at all to everybody else.
           */}
          <ExportPdfButton
            label="Download PDF"
            disabled={rows.length === 0}
            onExport={async () => {
              // The renderer, fetched by this click and never by the page view — the named
              // import means nothing else in the module is reachable from here.
              const { exportEventPdf } = await import('../features/reports/eventPdf');
              exportEventPdf({
                filter,
                actorName,
                orgName,
                logo: await letterheadLogo(),
                rows,
                total,
                generatedAt: new Date(),
                hasMore: report.hasNextPage,
              });
            }}
          />
          <Button size="sm" onClick={() => window.print()}>
            Print
          </Button>
        </>
      }
      controls={
        <Panel className="print:hidden">
          <PanelHeader
            title="Report selection"
            subtitle="Narrow the report to the events you want to see. Every filter is optional, and the range is inclusive."
          />
          <LogFilters
            filter={filter}
            today={today}
            actors={actors.data ?? []}
            categoryLegend="Event"
            onChange={(next: EventFilter) => {
              // `replace` so the back button leaves the report rather than walking every filter
              // change somebody made on the way to the range they wanted.
              void navigate({ search: next, replace: true });
            }}
          />
        </Panel>
      }
    >
      <div className="flex flex-col gap-4">
        {actors.isError ? (
          <LoadFailed retry={() => void actors.refetch()} retrying={actors.isFetching} />
        ) : null}
        {report.isError ? (
          <LoadFailed retry={() => void report.refetch()} retrying={report.isFetching} />
        ) : null}

        {report.isPending ? (
          <QueryLoading />
        ) : (
          <EventReport
            filter={filter}
            actorName={actorName}
            orgName={orgName}
            rows={rows}
            total={total}
            generatedAt={new Date()}
            isPending={report.isPending}
            hasMore={report.hasNextPage}
            isLoadingMore={report.isFetchingNextPage}
            onLoadMore={() => void report.fetchNextPage()}
          />
        )}
      </div>
    </ReportWorkspace>
  );
}
